import userService from "@/api/services/userService";
import { removeAccessToken } from "@/utils/storage";
import { POPUP_BUY_SUBSCRIPTION } from "@/utils/variables";
import router from "@/router/router";
import { changeLanguage } from "@/i18n";
import { defineStore } from "pinia";
import { Profile } from "@/interfaces/User/Profile.ts";
import { useFeatureStore } from "@/store/feature.ts";
import { usePopupStore } from "@/store/popup.ts";

interface UserState {
  profile: Profile | null;
  loggedIn: boolean;
  busy: boolean;
}

export const useUserStore = defineStore("user", {
  state: (): UserState => ({
    profile: null,
    loggedIn: false,
    busy: false,
  }),

  getters: {
    isLoggedIn: (state: UserState) => state.loggedIn,
    getProfile: (state: UserState) => state.profile,
    isPremium: (state: UserState) => {
      const now = new Date().getTime() / 1000;
      return state.profile?.premium_until && state.profile?.premium_until > now;
    },
    isOrganic: (state: UserState) => state.profile?.registration_campaign === null,
    isShowPlaydateDescriptionBlock: (state: UserState) => state.profile?.settings?.playdate_block || false,
    showUpcomingPlaydates: (state: UserState) => state.profile?.settings?.upcoming_playdate_block || false,
    isBusy: (state: UserState) => state.busy,
  },

  actions: {
    updateSettings(setting: Record<string, any>) {
      if (!this.profile) {
        return;
      }

      this.profile.settings = { ...this.profile.settings, ...setting };
    },

    chargeCoins(coins: number) {
      if (!this.profile) {
        return;
      }

      const newCoins = this.profile.coins - coins;
      this.profile = { ...this.profile, ...{ coins: newCoins } };
    },

    async updateEmail(email: string, password: string | null = null) {
      await userService.changeEmail(email, password);
      this.profile = { ...(this.profile as Profile), email };
    },

    async updateUsername(username: string, chatname: string, password: string) {
      await userService.changeUserName(username, chatname, password);
      this.profile = { ...(this.profile as Profile), username, chatname };
    },

    async updateNotifications(playdate_notification: boolean, camera_mail_notification: boolean) {
      await userService.changeNotifications(playdate_notification, camera_mail_notification);

      this.profile = { ...(this.profile as Profile), playdate_notification, camera_mail_notification };
    },

    async updateUserData(
      first_name: string | null,
      last_name: string | null,
      street: string | null,
      house: string | null,
      zip: number | null,
      city: string | null,
      country: number | null,
    ) {
      await userService.changeUserData(first_name, last_name, street, house, zip, city, country);

      this.profile = { ...(this.profile as Profile), first_name, last_name, street, house, zip, city, country };
    },

    async fetchProfile() {
      if (this.busy) {
        return;
      }

      this.busy = true;
      let userProfile = null;

      try {
        userProfile = await userService.getProfile();

        changeLanguage(userProfile.language);

        this.profile = userProfile;
        this.loggedIn = true;
      } catch (e: any) {
        removeAccessToken();
        this.profile = null;
        this.loggedIn = false;

        if (e.status?.status !== 401) {
          throw e;
        }
      } finally {
        this.busy = false;
      }

      return userProfile;
    },

    async logout(redirect?: string | Record<string, any>) {
      this.loggedIn = false;
      await removeAccessToken();

      usePopupStore().closeAll();

      if (redirect) {
        const url = typeof redirect === "string" ? { path: redirect, query: { logout: true } } : redirect;
        await router.push(url);
      }

      this.profile = null;
    },
    showSubscriptionPaywall(payload: Record<string, any> = {}) {
      if (useFeatureStore().hasAnyDiscountFeature) {
        router.push("/paywall");
      } else {
        usePopupStore().show(POPUP_BUY_SUBSCRIPTION, payload);
      }
    },
  },
});
